import React, { useState } from 'react';
import './Feedback.css';

const Feedback = () => {
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submissionId, setSubmissionId] = useState('');

    // Google Apps Script URL
    const GOOGLE_SHEETS_API_URL =
        'https://script.google.com/macros/s/AKfycbwIgRHM8PjxaCetEkejOY3CyZZ0MOTKCnyFeusGToR4PuGordhg27A29O-lFvEi13w/exec';

    // Star handlers
    const handleStarClick = (index) => {
        setRating(index + 1);
        setError('');
    };

    const handleStarHover = (index) => {
        setHoveredRating(index + 1);
    };

    const handleStarLeave = () => {
        setHoveredRating(0);
    };

    // Feedback input
    const handleFeedbackChange = (e) => {
        setFeedback(e.target.value);
    };

    // Send to Google Sheets
    const saveToGoogleSheets = async (data) => {
        try {
            console.log('📡 Sending to:', GOOGLE_SHEETS_API_URL);

            await fetch(GOOGLE_SHEETS_API_URL, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            return {
                success: true,
                message: 'Feedback submitted',
            };
        } catch (error) {
            console.error('Google Sheets Error:', error);
            throw error;
        }
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (rating === 0) {
            setError('Please select a rating.');
            return;
        }

        if (feedback.trim().length < 5) {
            setError(
                'Please share a bit more about your experience (minimum 5 characters).'
            );
            return;
        }

        setIsSubmitting(true);
        setError('');

        const feedbackData = {
            rating,
            feedback: feedback.trim(),
        };

        try {
            await saveToGoogleSheets(feedbackData);

            setSubmissionId(
                `BLU-${Date.now().toString().slice(-6)}`
            );

            setIsSubmitted(true);

            console.log('✅ Submitted:', feedbackData);
        } catch (error) {
            console.error(error);
            setError('Unable to submit feedback. Please try again.');
            setIsSubmitting(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setRating(0);
        setHoveredRating(0);
        setFeedback('');
        setError('');
        setIsSubmitted(false);
        setSubmissionId('');
        setIsSubmitting(false);
    };

    // Render stars
    const renderStars = () => {
        const currentRating = hoveredRating || rating;
        const stars = [];

        for (let i = 0; i < 5; i++) {
            const isFilled = i < currentRating;

            stars.push(
                <button
                    key={i}
                    type="button"
                    className={`star-btn ${isFilled ? 'filled' : 'empty'}`}
                    onClick={() => handleStarClick(i)}
                    onMouseEnter={() => handleStarHover(i)}
                    onMouseLeave={handleStarLeave}
                    disabled={isSubmitting || isSubmitted}
                    aria-label={`Rate ${i + 1}`}
                >
                    <svg
                        className="star-icon"
                        viewBox="0 0 24 24"
                        fill={isFilled ? '#FBBF24' : '#D1D5DB'}
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                            stroke={isFilled ? '#FBBF24' : '#D1D5DB'}
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            );
        }

        return stars;
    };

    // Success screen
    if (isSubmitted) {
        return (
            <div className="feedback-page">
                <header className="topbar">
                    <img
                        src="/BLU-Plus-Dark-Logo.svg"
                        alt="BLU+"
                        className="topbar-logo"
                    />
                </header>

                <div className="page-content success-content">
                    <div className="success-icon-wrapper">
                        <svg
                            className="success-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#10B981"
                                strokeWidth="2"
                            />
                            <path
                                d="M8 12L11 15L16 9"
                                stroke="#10B981"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>

                    <h2 className="success-title">
                        Thank You for Your Feedback!
                    </h2>

                    <p className="success-subtext">
                        Your response has been recorded.
                    </p>

                    {submissionId && (
                        <p className="submission-id">
                            Reference ID: <strong>{submissionId}</strong>
                        </p>
                    )}

                    <button
                        className="submit-btn reset-btn"
                        onClick={handleReset}
                    >
                        Submit Another Response
                    </button>
                </div>

                <footer className="footer">
                    <div className="footer-container">
                        <p>© 2026 BLU+. All rights reserved.</p>
                        <p>Powered by Kode Blue</p>
                    </div>
                </footer>
            </div>
        );
    }

    // Main form
    return (
        <div className="feedback-page">
            <header className="topbar">
                <div className="topbar-container">
                    <img
                        src="/BLU-Plus-Dark-Logo.svg"
                        alt="BLU+"
                        className="topbar-logo"
                    />
                </div>
            </header>

            <div className="page-content">
                <div className="content-wrapper">
                    <div className="thank-you-section">
                        <div className="thank-you-icon">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="#10B981"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M8 12L11 15L16 9"
                                    stroke="#10B981"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        <h1 className="thank-you-title">
                            Thank You for Choosing BLU+
                        </h1>

                        <p className="thank-you-subtext">
                            Your feedback helps us improve our emergency response services.
                        </p>
                    </div>

                    <form
                        className="feedback-form"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        <div className="form-group">
                            <label className="form-label">
                                How was your experience?
                            </label>

                            <div className="stars-container">
                                {renderStars()}
                            </div>

                            {rating > 0 && (
                                <div className="rating-helper">
                                    You rated {rating} star
                                    {rating > 1 ? 's' : ''}
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label
                                htmlFor="feedback-text"
                                className="form-label"
                            >
                                Tell us about your experience
                            </label>

                            <textarea
                                id="feedback-text"
                                className="feedback-textarea"
                                rows="4"
                                placeholder="Share your experience... (e.g., Crew behavior, vehicle cleanliness, response time)"
                                value={feedback}
                                onChange={handleFeedbackChange}
                                maxLength={500}
                                disabled={isSubmitting}
                                required
                            />

                            <div className="character-count">
                                {feedback.length}/500 characters
                            </div>
                        </div>

                        {error && (
                            <div
                                className="error-message"
                                role="alert"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className={`submit-btn ${rating === 0 || isSubmitting
                                    ? 'disabled'
                                    : ''
                                }`}
                            disabled={rating === 0 || isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner"></span>
                                    Submitting...
                                </>
                            ) : (
                                'Submit Feedback'
                            )}
                        </button>
                    </form>
                </div>
            </div>

            <footer className="footer">
                <div className="footer-container">
                    <p>© 2026 BLU+. All rights reserved.</p>
                    <p>Powered by Kode Blue</p>
                </div>
            </footer>
        </div>
    );
};

export default Feedback;